<template>
  <Form
    v-if="!hasBeenSubmitted"
    method="post"
    class="pt-6 pb-5"
    @submit="handleSubmit"
  >
    <h1 class="font-bold text-center text-3xl">
      {{ t('auth.request-invite') }}
    </h1>
    <p class="text-center font-semibold">
      {{ t('auth.request-invite-instructions') }}
    </p>

    <TextInput
      v-model="formData.fullName"
      :label="t('auth.full-name')"
      :placeholder="t('auth.full-name')"
      :rules="[isRequired, nameMaxLength]"
      @update:model-value="errorMessage = ''"
    />
    <TextInput
      v-model="formData.preferredName"
      :label="t('auth.preferred-name')"
      :placeholder="t('auth.preferred-name')"
      :rules="[isRequired, nameMaxLength]"
      @update:model-value="errorMessage = ''"
    />
    <TextInput
      v-model="formData.email"
      :label="t('auth.email')"
      :placeholder="t('auth.email')"
      disabled
    />
    <TextInput
      v-if="shouldDisplayCompany"
      v-model="formData.company"
      :label="companyLabel"
      :placeholder="t('auth.company')"
      :rules="companyRules"
      @update:model-value="errorMessage = ''"
    />
    <TextInput
      v-if="shouldDisplayWebsite"
      v-model="websiteValue"
      :label="websiteLabel"
      :placeholder="t('auth.website')"
      :rules="websiteRules"
      @update:model-value="errorMessage = ''"
    />
    <TextInput
      v-if="shouldDisplayPhone"
      v-model="phoneValue"
      :label="phoneLabel"
      :placeholder="t('auth.phone')"
      :rules="phoneRules"
      @update:model-value="errorMessage = ''"
    />

    <div v-if="visibleCustomInputs.length > 0">
      <TextInput
        v-for="[inputName, inputConfig] in visibleCustomInputs"
        :key="inputName"
        v-model="formData.customInputs[inputName]"
        :label="inputConfig.required ? inputName : `${inputName} (optional)`"
        :type="inputConfig.type"
        :rules="inputConfig.required ? [isRequired] : []"
        :placeholder="inputName"
        @update:model-value="errorMessage = ''"
      />
    </div>

    <Alert v-if="errorMessage" class="mt-4" :type="AlertType.ERROR" icon>
      {{ errorMessage }}
    </Alert>

    <Button
      :label="t('auth.request-invite')"
      class="w-full mt-10"
      type="submit"
    />
  </Form>

  <div v-else class="pt-14 pb-13">
    <h1 class="font-bold text-center text-3xl">
      {{ t('auth.request-invite-submitted-title') }}
    </h1>
    <p class="text-center font-semibold">
      {{ t('auth.request-invite-submitted-message') }}
    </p>
  </div>

  <Button
    :label="t('auth.back-to-sign-in')"
    :variant="ButtonVariant.TEXT"
    :color="hasBeenSubmitted ? ButtonColor.PRIMARY : ButtonColor.SECONDARY"
    class="w-full mb-14"
    @click="emit('change-type', SelfRegisterType.SIGN_IN)"
  />
</template>

<script setup lang="ts">
import { computed, ref } from '#imports';
import { Form } from 'vee-validate';
import { TextInput } from '~/components/inputs/text-input';
import {
  Button,
  ButtonColor,
  ButtonVariant,
  Alert,
  AlertType,
} from '@exfluential/ui';
import { isRequiredI18n, maxLength } from '~/utils/validators';
import { useLoader } from '~/composables/use-loader';
import { SelfRegisterType } from '~/api/auth';
import { apiRequestInvitation, RequestInvitationDto } from '~/api/accounts';
import { storeToRefs } from 'pinia';
import { useOrganizationStore } from '~/store/organization';

type Props = {
  modelValue: string;
};
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'change-type', value: SelfRegisterType): void;
}>();

const { t } = useI18n();

const organizationStore = useOrganizationStore();
const { organization } = storeToRefs(organizationStore);

const isRequired = isRequiredI18n(t);

const errorMessage = ref('');

const websiteValue = ref('');
const phoneValue = ref('');

const formData = ref<RequestInvitationDto>({
  fullName: '',
  preferredName: '',
  company: '',
  email: props.modelValue,
  customInputs: {} as Record<string, string>,
});

const hasBeenSubmitted = ref<boolean>(false);

const nameMaxLength = maxLength(200);
const companyMaxLength = maxLength(100);
const websiteMaxLength = maxLength(2048);
const phoneMaxLength = maxLength(50);

const companyConfig = computed(
  () => organization.value?.organizationSettings?.customInputs?.Company,
);

const shouldDisplayCompany = computed(
  () => companyConfig.value?.display !== false,
);

const isCompanyRequired = computed(
  () => companyConfig.value?.required === true,
);

const companyLabel = computed(() =>
  isCompanyRequired.value
    ? t('auth.company')
    : `${t('auth.company')} (optional)`,
);

const companyRules = computed(() => {
  const rules = [companyMaxLength];
  if (isCompanyRequired.value) {
    rules.unshift(isRequired);
  }
  return rules;
});

const websiteConfig = computed(
  () => organization.value?.organizationSettings?.customInputs?.Website,
);

const shouldDisplayWebsite = computed(
  () => websiteConfig.value?.display !== false,
);

const isWebsiteRequired = computed(
  () => websiteConfig.value?.required === true,
);

const websiteLabel = computed(() =>
  isWebsiteRequired.value
    ? t('auth.website')
    : `${t('auth.website')} (optional)`,
);

const websiteRules = computed(() => {
  const rules = [websiteMaxLength];
  if (isWebsiteRequired.value) {
    rules.unshift(isRequired);
  }
  return rules;
});

const phoneConfig = computed(
  () => organization.value?.organizationSettings?.customInputs?.Phone,
);

const shouldDisplayPhone = computed(() => phoneConfig.value?.display !== false);

const isPhoneRequired = computed(() => phoneConfig.value?.required === true);

const phoneLabel = computed(() =>
  isPhoneRequired.value ? t('auth.phone') : `${t('auth.phone')} (optional)`,
);

const phoneRules = computed(() => {
  const rules = [phoneMaxLength];
  if (isPhoneRequired.value) {
    rules.unshift(isRequired);
  }
  return rules;
});

const visibleCustomInputs = computed(() => {
  const inputs = organization.value?.organizationSettings?.customInputs;
  if (!inputs) return [];
  return Object.entries(inputs).filter(
    ([, config]: [string, any]) =>
      !config.isDefault && config.display !== false,
  );
});

const handleSubmit = async () => {
  await useLoader({
    action: async () => {
      const customInputs = { ...formData.value.customInputs };
      if (shouldDisplayWebsite.value && websiteValue.value) {
        customInputs.Website = websiteValue.value;
      }
      if (shouldDisplayPhone.value && phoneValue.value) {
        customInputs.Phone = phoneValue.value;
      }

      await apiRequestInvitation({
        ...formData.value,
        company: shouldDisplayCompany.value
          ? formData.value.company || null
          : null,
        customInputs,
      });
      hasBeenSubmitted.value = true;
    },
    onError: (err: any) => {
      errorMessage.value = err?.data?.message ?? 'Auth failed';
    },
  });
};
</script>
